import { RefObject, useEffect, useMemo, useRef } from "react";
import { useRegistrationV2 } from "./channel.hooks";
import { useGetEvent } from "./session.hooks";
import { ERegistrationLayoutTypes, ERegistrationStyle } from "types/working-model";
import { useTypedSelector } from "store/reducers/use-typed-selector";
import { useTranslate } from "i18n/useTranslationModules";
import i18n from "i18next";
import { announce } from "utils/announce";
import { LOGGED_OUT_KEY } from "utils/deregister";
import { getSessionStorageItem, removeSessionStorageItem } from "utils/session-storage";

export const useIsRegistrationV2 = (): boolean => {
	const event = useGetEvent();
	const registrationFlagV2 = useRegistrationV2();

	const isRegistrationV2 = true; //event?.settings?.is_registration_v2;

	return !!(isRegistrationV2 && registrationFlagV2);
};


export const useGetRegistrationLayout = (registrationStyle?: ERegistrationStyle) => {
	const isRegistrationV2 = useIsRegistrationV2();
	return useMemo(() => {
		// if reg v2, return the existing layout or aboveTheFoldR
		if (isRegistrationV2) {
			// if we have a valid v1 layout in the database and it's left, let's return aboveTheFoldL to keep it as close as possible
			// to the originally selected v1 layout
			if (registrationStyle?.layout === ERegistrationLayoutTypes.formLeft) {
				return ERegistrationLayoutTypes.aboveTheFoldL;
			}

			return registrationStyle?.layout || ERegistrationLayoutTypes.aboveTheFoldR;
		}

		// else if not reg v2
		// if we already have a valid v1 layout, return it
		if (registrationStyle?.layout && [ERegistrationLayoutTypes.formLeft, ERegistrationLayoutTypes.formRight].includes(registrationStyle.layout as ERegistrationLayoutTypes)) {
			return registrationStyle.layout;
		}

		// if we don't have a valid v1 layout, return formRight
		return ERegistrationLayoutTypes.formRight;
	}, [isRegistrationV2, registrationStyle?.layout]);
};

export const useAnnounceRegistrationSuccess = (hasAccess: boolean) => {
	const hasSubmittedRegistrationForm = useTypedSelector(state => state.LiveEventReducer.hasSubmittedRegistrationForm);
	const registrationSignInSuccess = useTypedSelector(state => state.LiveEventReducer.registrationSignInSuccess);
	const { t } = useTranslate('homepage');
	const hadAccess = useRef(hasAccess);

	useEffect(() => {
		const gainedAccess = hasAccess && !hadAccess.current;
		hadAccess.current = hasAccess;
		if (!gainedAccess || !hasSubmittedRegistrationForm || registrationSignInSuccess) return;
		announce(t('registration.RegistrationSuccessful', 'Registration successful. You are now logged in.'), 'assertive');
	}, [hasAccess, hasSubmittedRegistrationForm, registrationSignInSuccess, t]);
};

export const useAnnounceLogout = (language: string) => {
	const translationUpdates = useTypedSelector(state => state.LiveEventReducer.translationUpdates);
	const { t } = useTranslate('homepage');

	useEffect(() => {
		if (!getSessionStorageItem(LOGGED_OUT_KEY)) return;
		if (i18n.language !== language || !i18n.hasResourceBundle(language, 'homepage')) return;
		removeSessionStorageItem(LOGGED_OUT_KEY);
		announce(t('LogoutSuccessful', 'Logout successful. You are now logged out.'), 'assertive');
	}, [language, translationUpdates, t]);
};

export const useFocusOnStepChange = (targetRef: RefObject<{ focus: () => void } | null>) => {
	const registrationStep = useTypedSelector(state => state.LiveEventReducer.registrationStep);
	const hasSubmittedRegistrationForm = useTypedSelector(state => state.LiveEventReducer.hasSubmittedRegistrationForm);

	useEffect(() => {
		if (!hasSubmittedRegistrationForm) return;
		targetRef.current?.focus();
	}, [targetRef, hasSubmittedRegistrationForm, registrationStep]);
};
